/**
 * Task-queue API client — Conductor Phase 4 (scheduler / capacity queue).
 *
 * Wraps the gateway routes registered in chaos-gateway/src/task-queue.ts
 * (registerTaskQueueRoutes): the per-card live queue probe + the account
 * queue list + the three controls (force-start / cancel / switch-model via
 * PATCH). Every route is gated server-side by SCHEDULER_ENABLED; when the
 * gateway flag is OFF the probe returns `{ queue: null }` and the list /
 * controls 404. The UI side is gated by env.VITE_SCHEDULER_V2 — callers only
 * mount these queries when that flag is ON, and they degrade gracefully on a
 * 404 (older gateway / flag-OFF) so a UI/gateway flag mismatch never errors.
 *
 * Wire shape mirrors rowToApi() in task-queue.ts exactly — keep the two in
 * lock-step.
 */
import { apiJson } from './client';

/** A live queue row as serialized by the gateway's rowToApi(). */
export interface QueueItem {
  id: string;
  cardId: string;
  sessionId: string | null;
  orgId: string | null;
  boardId: string | null;
  enqueuedBy: string | null;
  state: 'queued' | 'admitting' | 'admitted' | 'cancelled' | 'failed';
  prompt: string;
  vendor: 'anthropic' | 'openai' | 'google' | null;
  model: string | null;
  effort: string | null;
  permissionMode: string | null;
  priority: 'high' | 'mid' | 'low';
  /**
   * Why the item is still waiting (drives the ETA/reason copy). NULL once it
   * is admittable. Mirrors the migration CHECK task_queue_reason_check.
   */
  blockedReason:
    // 0325 / 0326 (both applied 2026-08-13) — the W4 admission and single-payer
    // slot vocabulary. No gateway gate writes one yet: the migrations shipped
    // ahead of their TypeScript, so these are in the column and in nothing else.
    // Named here anyway, for the reason the 0177 block below already gives — the
    // list that decides the wording must not be narrower than the list the
    // database accepts. Waiting for the writer is how `session_busy` cost three
    // weeks and how the brakes cost a silent requeue loop.
    | 'execution_identity_unattributed'
    | 'no_execution_credential'
    | 'vendor_execution_disabled'
    | 'plan_configuration_missing'
    | 'account_slots'
    | 'workspace_slots'
    | 'spawn_rate'
    // 0088 — the six capacity reasons this list started with.
    | 'max_concurrent'
    | 'ram'
    | 'cpu'
    | 'window_5h'
    | 'window_weekly'
    | 'window_exhausted'
    // 0161 — legal in the database since 2026-07-11 and unwritten until the
    // English for it existed, because it did not.
    | 'session_busy'
    // 0177 — what `evaluateAdmitBrakes()` has been trying to write. These three
    // only ever land together with `state='failed'`, and both queue read routes
    // filter `state IN ('queued','admitting')`, so today they do not reach a
    // screen. They are named anyway: the list that decides the wording must not
    // be narrower than the list the database accepts, which is the whole reason
    // this file needed a change at all.
    | 'card_run_ceiling'
    | 'card_token_budget'
    | 'brake_unavailable'
    // 0398 (M4) — the two card waits of continuous sessions: another step is
    // running on the card, or a runtime of the card is busy / not answering.
    // Written only while the gateway's continuous-sessions flag is on.
    | 'card_step_in_progress'
    | 'card_runtime_busy'
    | null;
  /** ISO-8601 next-window reset used for the ETA copy, or null. */
  etaAt: string | null;
  admittedRunId: string | null;
  enqueuedAt: string;
  admittedAt: string | null;
  attemptCount: number;
  lastError: string | null;
}

/**
 * WP-4a (B-22): one of the viewer's OWN queued messages the gateway refused at
 * dispatch, because the session it was queued into runs on another person's
 * AI subscription. The whole prompt, so the card can show it and resend it on
 * the viewer's own session. Only present while the gateway's actor/executor
 * guard is on; absent otherwise.
 */
export interface ReturnedQueueMessage {
  id: string;
  prompt: string;
  vendor: string | null;
  enqueuedAt: string;
  failedAt: string | null;
}

/** Vendor override accepted by the "Switch model" PATCH. */
export type QueueVendor = 'anthropic' | 'openai' | 'google';

export interface QueuePatchBody {
  prompt?: string;
  model?: string | null;
  effort?: string | null;
  vendor?: QueueVendor;
}

export const taskQueueApi = {
  /**
   * The card's single live (queued/admitting) row, or null. Returns
   * `{ queue: null }` when SCHEDULER_ENABLED is OFF server-side — so this is
   * safe to call against a flag-OFF gateway (no throw).
   */
  forCard: (cardId: string) =>
    apiJson<{ queue: QueueItem | null; returned?: ReturnedQueueMessage[] }>(
      `/cards/${encodeURIComponent(cardId)}/queue`,
    ),

  /** Account-wide queued list in scheduler order (priority → FIFO). */
  list: () => apiJson<{ items: QueueItem[] }>(`/queue`),

  /** Admit now, bypassing soft caps (still refuses on a dead vendor window). */
  forceStart: (cardId: string, id: string) =>
    apiJson<{ ok: true; forced: true }>(
      `/cards/${encodeURIComponent(cardId)}/queue/${encodeURIComponent(id)}/force-start`,
      { method: 'POST' },
    ),

  /** Edit prompt / model / effort / vendor (re-gates against the new vendor). */
  patch: (cardId: string, id: string, body: QueuePatchBody) =>
    apiJson<{ queue: QueueItem | null }>(
      `/cards/${encodeURIComponent(cardId)}/queue/${encodeURIComponent(id)}`,
      { method: 'PATCH', json: body },
    ),

  /**
   * WP-4a (B-22): the viewer resent a returned message on their own session,
   * or dismissed it — it leaves the card's returned list.
   */
  acknowledgeReturned: (cardId: string, id: string) =>
    apiJson<{ ok: true; acknowledged: true }>(
      `/cards/${encodeURIComponent(cardId)}/queue/${encodeURIComponent(id)}/acknowledge`,
      { method: 'POST' },
    ),

  /** Cancel → the prompt is preserved as a deferred prompt server-side. */
  cancel: (cardId: string, id: string) =>
    apiJson<{ ok: true; cancelled: true; deferredPromptId: string | null }>(
      `/cards/${encodeURIComponent(cardId)}/queue/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
};

export const taskQueueKeys = {
  all: ['task-queue'] as const,
  forCard: (cardId: string) => ['task-queue', 'card', cardId] as const,
  list: () => ['task-queue', 'list'] as const,
};

// ── Plain-English reason + ETA copy (single source) ────────────────────
// Shared by the board card tooltip, the composer "Queued" notice, and the
// card-detail chip so the wording never drifts between surfaces. Founder rule:
// plain language, no jargon (the founder is not a developer). G7: a
// window-blocked card shows "waiting on limit + ETA(next reset)".

const REASON_LABEL: Record<NonNullable<QueueItem['blockedReason']>, string> = {
  // 0325 / 0326. Three are waits on a pool that refills on its own; four are
  // stops that need a person to change something, and the wording says which,
  // because "waiting" under a row that will never move is worse than no line.
  account_slots: 'waiting for a free slot on this account',
  workspace_slots: 'waiting for a free slot in this workspace',
  spawn_rate: 'waiting — this plan starts only so many runs an hour',
  no_execution_credential: 'stopped — no login is connected to run this',
  vendor_execution_disabled: 'stopped — this model provider is turned off',
  // ONE TOKEN, TWO SURFACES. `plan_configuration_missing` is also a key in
  // `billing.ts`'s UNRESOLVED_REASON, where it explains why a usage METER cannot
  // be resolved ("no plan row, so there is no norm to measure against"). Same
  // underlying fact, different question, so the wording differs on purpose — but
  // they must never come to CONTRADICT each other. If one changes meaning, read
  // the other.
  plan_configuration_missing: 'stopped — this plan has no limits set up yet',
  execution_identity_unattributed: 'stopped — nobody is set to pay for this run',
  max_concurrent: 'waiting for a free slot',
  ram: 'waiting for memory to free up',
  cpu: 'waiting for the server to cool down',
  window_5h: 'waiting on the model usage limit',
  window_weekly: 'waiting on the weekly usage limit',
  window_exhausted: 'waiting on the model usage limit',
  // 0161. The card is mid-turn; this prompt goes next, nothing is wrong.
  session_busy: 'waiting for this card to finish its turn',
  // 0177. The three the admit brakes raise. Each is a STOP, not a wait, so the
  // wording says stopped — a line reading "waiting" under a row that will never
  // move on its own is worse than no line at all.
  card_run_ceiling: 'stopped — this card hit its run limit',
  card_token_budget: 'stopped — this card used up its token budget',
  brake_unavailable: 'stopped — the safety check could not run',
  // 0398 (M4). One step runs on a card at a time; this one goes next.
  card_step_in_progress: 'waiting for the step running on this card to finish',
  // The runtime fence: a previous step may still be running somewhere.
  card_runtime_busy: 'waiting — a previous step may still be running',
};

/**
 * Anything the map has not been taught, said in English rather than as
 * `undefined`.
 *
 * THE FAILURE THIS ENDS. `queueReasonText` indexed REASON_LABEL directly and
 * returned whatever came back. The map knew the six values migration 0088
 * created; `session_busy` became storable in 0161 (three weeks) and 0177 adds
 * three more. The first row to carry any of them would have put
 * "Queued — undefined" on the founder's board.
 *
 * The labels above close today's gap. THIS closes the next one, which matters
 * more: the reason list has grown three times and will grow again, and whoever
 * widens the database CHECK will not come looking for a map in the UI's API
 * layer — that is precisely what happened here, twice. So an unrecognised
 * reason degrades to the enum with its underscores opened out
 * ("card run ceiling"), which is legible without a developer, correct for every
 * future value nobody has written yet, and impossible to confuse with a label
 * somebody chose.
 */
function fallbackReasonLabel(reason: string): string {
  const words = reason.replace(/[_-]+/g, ' ').trim();
  return words ? `waiting on ${words}` : 'queued';
}

/**
 * Short relative-time formatter for an ETA in the FUTURE. "in 3m" / "in 2h" /
 * "in 1d". Returns '' for a null / past / unparseable value so callers can
 * omit the ETA clause cleanly.
 */
export function formatEta(etaAt: string | null | undefined, now: Date = new Date()): string {
  if (!etaAt) return '';
  const ms = Date.parse(etaAt) - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'in under a minute';
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days}d`;
}

/** The reason clause alone, e.g. "waiting for a free slot". */
export function queueReasonText(reason: QueueItem['blockedReason']): string {
  if (!reason) return 'queued';
  // Indexed through a lookup, NOT `REASON_LABEL[reason]` — the parameter is
  // typed, the JSON is not, and this map has been a version behind the database
  // twice already. `?? fallback` is what stops the third time reaching a screen.
  return REASON_LABEL[reason] ?? fallbackReasonLabel(reason);
}

/**
 * One-line human status for a queued item: reason + ETA when known.
 * e.g. "Queued — waiting on the model usage limit · starts in 2h" or, when no
 * ETA is known, "Queued — waiting for a free slot".
 */
export function queueStatusLine(item: Pick<QueueItem, 'blockedReason' | 'etaAt'>): string {
  const reason = queueReasonText(item.blockedReason);
  const eta = formatEta(item.etaAt);
  if (eta) return `Queued — ${reason} · starts ${eta}`;
  return `Queued — ${reason}`;
}
